import * as React from 'react';
import { checkUserSession } from './apibridge/checkUserSession';

// API
import apibridge from './apibridge';

// PACKAGES
import { Switch, Route, Redirect, useLocation, useHistory } from 'react-router-dom';

// UTILS
import { queryStringToObject, usePrevious, validationErrorsToString } from './utils';

// ANALYTICS
import GoogleAnalyticsHelper from './utils/GoogleAnalyticsHelper';

// REDUX
import { useAppSelector, useAppDispatch } from './store';
import { UserType } from './store/slices/User';
import UserSlice from './store/slices/User';
import ListsSlice from './store/slices/Lists';

// COMPONENTS
import LoadingSpinner from './components/LoadingSpinner';

// LAYOUT
import LayoutTwoColumn from './components/Layout/LayoutTwoColumn';
import LayoutOneColumn from './components/Layout/LayoutOneColumn';

// PAGES
import CreateAccount from './pages/CreateAccount/CreateAccount';
import ConfirmEmail from './pages/ConfirmEmail/ConfirmEmail';
import Login from './pages/Login/Login';
import ForgotPassword from './pages/ForgotPassword/ForgotPassword';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import Plans from './pages/Plans/Plans';
import RegisterOrganisation from './pages/RegisterOrganisation/RegisterOrganisation';
import AddCertification from './pages/AddCertification/AddCertification';
import FindOrganisation from './pages/FindOrganisation/FindOrganisation';

import Dashboard from './pages/Dashboard/Dashboard';
import MyAccount from './pages/MyAccount/MyAccount';
import LinkAccount from './pages/LinkAccount/LinkAccount';

import Staff from './pages/Staff/Staff';
import StaffProfile from './pages/StaffProfile/StaffProfile';

import LeadershipTeam from './pages/LeadershipTeam/LeadershipTeam';

import Families from './pages/Families/Families';
import FamiliesProfile from './pages/FamiliesProfile/FamiliesProfile';

import Visitors from './pages/Visitors/Visitors';
import VisitorsProfile from './pages/VisitorsProfile/VisitorsProfile';

import RequestsToJoin from './pages/RequestsToJoin/RequestsToJoin';
import RequestsToJoinDetail from './pages/RequestsToJoinDetail/RequestsToJoinDetail';

import InviteToJoin from './pages/InviteToJoin/InviteToJoin';

import ChildSafeStandards from './pages/ChildSafeStandards/ChildSafeStandards';
import ChildSafeStandardsDetail from './pages/ChildSafetyStandardsDetail/ChildSafeStandardsDetail';

import Training from './pages/Training/Training';
import TrainingDetail from './pages/TrainingDetail/TrainingDetail';

import SafetyChecklists from './pages/SafetyChecklists/SafetyChecklists';
import SafetyChecklistsNewChecklist from './pages/SafetyChecklistsNewChecklist/SafetyChecklistsNewChecklist';
import SafetyChecklistsTemplatesPreview from './pages/SafetyChecklistsTemplatesPreview/SafetyChecklistsTemplatesPreview';
import SafetyChecklistsSubmit from './pages/SafetyChecklistsSubmit/SafetyChecklistsSubmit';
import SafetyChecklistsDetail from './pages/SafetyChecklistsDetail/SafetyChecklistsDetail';

import Policies from './pages/Policies/Policies';
import PolicyTemplates from './pages/PolicyTemplates/PolicyTemplates';
import PolicySubmit from './pages/PolicySubmit/PolicySubmit';

import Feedback from './pages/Feedback/Feedback';
import FeedbackDetail from './pages/FeedbackDetail/FeedbackDetail';
import FeedbackSubmit from './pages/FeedbackSubmit/FeedbackSubmit';

import IncidentReports from './pages/IncidentReports/IncidentReports';
import IncidentReportsDetailArchive from './pages/IncidentReportsDetailArchive/IncidentReportsDetail';
import IncidentReportsDetail from './pages/IncidentReportsDetail/IncidentReportsDetail';
import IncidentSubmitArchive from './pages/IncidentSubmitArchive/IncidentSubmit';

import Complaints from './pages/Complaints/Complaints';
import ComplaintsDetail from './pages/ComplaintsDetail/ComplaintsDetail';
import ComplaintsAdviceTemplates from './pages/ComplaintsAdviceTemplates/ComplaintsAdviceTemplates';
import ComplaintsSubmit from './pages/ComplaintsSubmit/ComplaintsSubmit';

import Audit from './pages/Audit/Audit';
import Settings from './pages/Settings/Settings';

import HelpDesk from './pages/HelpDesk/HelpDesk';
import HelpDeskDetail from './pages/HelpDeskDetail/HelpDeskDetail';

import Companies from './pages/Companies/Companies';
import CompaniesDetail from './pages/CompaniesDetail/CompaniesDetail';

import ContentTrainingModule from './pages/ContentTrainingModule/ContentTrainingModule';
import ContentTrainingModuleDetail from './pages/ContentTrainingModuleDetail/ContentTrainingModuleDetail';

import ContentPolicyTemplates from './pages/ContentPolicyTemplates/ContentPolicyTemplates';
import ContentPolicyTemplatesDetail from './pages/ContentPolicyTemplatesDetail/ContentPolicyTemplatesDetail';

import ContentChecklistTemplates from './pages/ContentChecklistTemplates/ContentChecklistTemplates';
import ContentChecklistTemplatesDetail from './pages/ContentChecklistTemplatesDetail/ContentChecklistTemplatesDetail';

import ContentComplaintsAdvice from './pages/ContentComplaintsAdvice/ContentComplaintsAdvice';
import ContentComplaintsAdviceDetail from './pages/ContentComplaintsAdviceDetail/ContentComplaintsAdviceDetail';

import ContentChildSafeStandards from './pages/ContentChildSafeStandards/ContentChildSafeStandards';
import ContentChildSafeStandardsDetail from './pages/ContentChildSafeStandardsDetail/ContentChildSafeStandardsDetail';

import ContentHelpDesk from './pages/ContentHelpDesk/ContentHelpDesk';
import ContentHelpDeskDetail from './pages/ContentHelpDeskDetail/ContentHelpDeskDetail';

import ConfirmNewEmail from './pages/MyAccount/ConfirmNewEmail';
import LandingDashboard from './pages/LandingDashboard/LandingDashboard';
import ProfileError from './pages/Errors/ProfileError';
import LinkSuccess from './pages/LinkAccount/LinkSuccess';
import IncidentReportLinking from './pages/IncidentReportLinking/IncidentReportLinking';
import Attachment from './pages/Attachment/Attachment';
import RegisterOrganisationSummary from './pages/RegisterOrganisation/RegisterOrganisationSummary';
import ParentConfirm from './pages/CreateAccount/ParentConfirm';
import TwoFactorAuthentication from './pages/Login/TwoFactorAuthentication';
import TrainingSubmit from './pages/TrainingSubmit/TrainingSubmit';
import IncidentSubmit from './pages/IncidentSubmit/IncidentSubmit';
import IncidentReportLinkingArchive from './pages/IncidentReportLinkingArchive/IncidentReportLinkingArchive';

const App: React.FunctionComponent = () => {
	const location = useLocation();
	const dispatch = useAppDispatch();
	const [mounted, setMounted] = React.useState<boolean>(false);
	const [loading, setLoading] = React.useState<boolean>(true);
	const returnUrl = React.useRef<string>('');
	const user: UserType = useAppSelector((state) => state.user);
	const previousUserState: UserType = usePrevious(user);
	const history = useHistory();

	const authenticateUserFromToken = async () => {
		setLoading(true);

		// on page first load or refresh - if localstorage has token - they are/were loggedIn
		let hasToken = !!localStorage.getItem('token');

		if (!hasToken) {
			setLoading(false);
			return;
		}

		// authenticate token
		const response = await apibridge.getUserDetails();

		if (response.isError) {
			dispatch(UserSlice.actions.logOut());
		} else if (response.result) {
			await apibridge.refreshAuthenticationToken();

			const data = {
				...response.result,
				forceUpdate: false,
				loggedIn: true
			};
			dispatch(UserSlice.actions.update(data));
		}

		// setloading(false) for returning users/hasToken has been moved to useeffect
		// as it needs to happen after user details are populated or redirects misfire
	};

	const createReturnUrl = () => {
		if (
			!user.loggedIn &&
			location.pathname &&
			location.pathname.indexOf('//') === -1 &&
			location.pathname.indexOf('/') === 0 &&
			location.pathname.length > 1
		) {
			returnUrl.current = '?returnUrl=' + location.pathname + encodeURIComponent(location.search);
		}
	};

	const getGlobalLists = async () => {
		const response = await apibridge.getGlobalLookups();

		if (response.result) {
			dispatch(ListsSlice.actions.update(response.result));
		}
	};

	React.useEffect(() => {
		history.listen(() => {
			if (checkUserSession() === false) {
				setLoading(true);
			}
		});
	}, []);

	React.useEffect(() => {
		// page changed

		returnUrl.current = '';
		const options = { top: 0, left: 0, behaviour: 'smooth' };
		window.scrollTo !== undefined ? window.scrollTo(options) : window.scroll(options);

		// push page view to GA tracking
		GoogleAnalyticsHelper.sendPageView();
	}, [location]);

	React.useEffect(() => {
		// this will run on first mount and every time the user store object changes
		if (!mounted) {
			setMounted(true);
			createReturnUrl();
			getGlobalLists();
			authenticateUserFromToken();
		} else {
			// after component has mounted and user has updated
			setLoading(false);

			checkForSwitchProfile();

			let userDataHasChanged = false;

			if (user.loggedIn && !user.id) {
				// user has just logged in but not retrieved complete user data
				userDataHasChanged = true;
			} else if (previousUserState.loggedIn && user.loggedIn && previousUserState.id) {
				// user is logged in and has all data

				if (user.forceUpdate) {
					userDataHasChanged = true;
				}
			}

			if (userDataHasChanged) {
				getGlobalLists();
				authenticateUserFromToken();
			}
		}
	}, [user]);

	const checkForSwitchProfile = async () => {
		const queryParams = queryStringToObject();
		const switchProfileId = queryParams.switchProfileId || undefined;
		const currentActiveProfileId = user.activeProfileId || undefined;
		const userProfiles = user.profiles;

		//Check if user is logged in, switchProfileId exists and userProfiles exist
		if (user.loggedIn && switchProfileId && userProfiles && userProfiles.length > 0) {
			//Check if the current profile is not already active and also that the profile exists for the user
			if (currentActiveProfileId !== switchProfileId && userProfiles?.filter(({ id }) => id === switchProfileId)[0]) {
				const response = await apibridge.doSwitchOrganisation(switchProfileId);
				if (response.isError) {
					history.push({
						pathname: '/profile-error',
						state: {
							error: response.validationErrors.length
								? validationErrorsToString(response.validationErrors)
								: response.message || 'An error has occurred'
						}
					});
				} else {
					getGlobalLists();
					authenticateUserFromToken();
				}
			} else if (!userProfiles?.filter(({ id }) => id === switchProfileId)[0]) {
				history.push({
					pathname: '/profile-error',
					state: { error: 'No profile associated with requested organisation' }
				});
			} else {
				return;
			}
		}
	};

	// special case for logging out directly after a hard refresh
	if (previousUserState.loggedIn && !user.loggedIn) {
		returnUrl.current = '';
	}

	if (loading) {
		return <LoadingSpinner show={loading} />;
	} else {
		return (
			<Switch>
				{/* --- ############## --- */}
				{/* --- EXTERNAL PAGES --- */}
				{/* --- ############## --- */}

				<Route exact path="/plans">
					<LayoutOneColumn className="header-alt">
						<Plans />
					</LayoutOneColumn>
				</Route>

				<Route exact path="/register-organisation">
					{user.loggedIn ? (
						<LayoutOneColumn>
							<RegisterOrganisation />
						</LayoutOneColumn>
					) : (
						<Redirect to="/plans" />
					)}
				</Route>

				<Route exact path="/register-organisation-payment">
					<LayoutOneColumn>
						<RegisterOrganisationSummary />
					</LayoutOneColumn>
				</Route>

				<Route exact path="/find-organisation">
					<LayoutOneColumn>
						<FindOrganisation />
					</LayoutOneColumn>
				</Route>

				<Route path="/login">
					{user.loggedIn ? (
						<Redirect to="/dashboard" />
					) : (
						<LayoutOneColumn>
							<Login />
						</LayoutOneColumn>
					)}
				</Route>

				<Route path="/two-factor-authentication" exact>
					<LayoutOneColumn>
						<TwoFactorAuthentication />
					</LayoutOneColumn>
				</Route>

				<Route exact path="/create-account">
					<LayoutOneColumn>
						<CreateAccount />
					</LayoutOneColumn>
				</Route>

				<Route exact path="/confirm-email">
					<LayoutOneColumn>
						<ConfirmEmail />
					</LayoutOneColumn>
				</Route>

				<Route exact path="/confirm-parent">
					<LayoutOneColumn>
						<ParentConfirm />
					</LayoutOneColumn>
				</Route>

				{/* Incident Report Linking Landing Page */}
				<Route exact path="/incident-archive-invite">
					<LayoutOneColumn>
						<IncidentReportLinkingArchive />
					</LayoutOneColumn>
				</Route>

				<Route exact path="/incident-invite">
					<LayoutOneColumn>
						<IncidentReportLinking />
					</LayoutOneColumn>
				</Route>

				{/* ---  Error Pages --- */}

				<Route exact path="/profile-error">
					<LayoutOneColumn>
						<ProfileError />
					</LayoutOneColumn>
				</Route>

				{/* // temporary redirect  */}
				<Route path="/account/emailverification">
					<Redirect to={{ ...location, pathname: '/confirm-email' }} />
				</Route>

				<Route exact path="/forgot-password">
					<LayoutOneColumn>
						<ForgotPassword />
					</LayoutOneColumn>
				</Route>

				<Route exact path="/reset-password">
					<LayoutOneColumn>
						<ResetPassword />
					</LayoutOneColumn>
				</Route>

				{/* // temporary redirect  */}
				<Route path="/account/resetpassword">
					<Redirect to={{ ...location, pathname: '/reset-password' }} />
				</Route>

				<Route exact path="/forgot-childaware-id">
					<LayoutOneColumn>
						<ForgotPassword />
					</LayoutOneColumn>
				</Route>

				<Route path="/change-email">
					<LayoutOneColumn>
						<ConfirmNewEmail />
					</LayoutOneColumn>
				</Route>

				{/* Attachment Route */}
				<Route path="/attachment">
					<LayoutOneColumn>
						<Attachment />
					</LayoutOneColumn>
				</Route>

				{/* --- if none of the above routes are satisfied then redirect --- */}
				{!user.loggedIn && (
					<Route path="/">
						{/* <Redirect to="/login" /> */}
						<Redirect to={'/login' + returnUrl.current} />
					</Route>
				)}

				{/* --- ############## --- */}
				{/* --- INTERNAL PAGES --- */}
				{/* --- ############## --- */}

				{/* ---  Link-accounts --- */}
				<Route exact path="/link-accounts">
					<LayoutOneColumn>
						<LinkAccount />
					</LayoutOneColumn>
				</Route>

				<Route exact path="/link-success">
					<LayoutOneColumn>
						<LinkSuccess />
					</LayoutOneColumn>
				</Route>

				{/* DASHBOARD */}
				<Route exact path="/dashboard">
					<LayoutTwoColumn showLogo={true}>
						<Dashboard />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/landingDashboard">
					<LayoutOneColumn>
						<LandingDashboard />
					</LayoutOneColumn>
				</Route>

				{/* PEOPLE SECTION */}
				<Route exact path="/my-account/:id">
					<LayoutTwoColumn title="My account">
						<MyAccount />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/staff">
					<LayoutTwoColumn title="Staff & Volunteers">
						<Staff />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/staff/:id">
					<LayoutTwoColumn title="Staff profile" showBack={true}>
						<StaffProfile />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/leadershipteam">
					<LayoutTwoColumn
						title="Leadership Team"
						text={`It is important for organisations to select a team of leaders who will be responsible for managing child safety. Child safety should never be the responsibility of one person. ChildAware recommends a leadership team of 3-8 members. Your child safety leadership team will receive feedback and complaints and will be responsible for the organisation’s response. A strong child safety leadership team is critical to good governance and to your organisation being ChildAware.`}
					>
						<LeadershipTeam />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/families">
					<LayoutTwoColumn title="Families">
						<Families />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/families/:id">
					<LayoutTwoColumn>
						<FamiliesProfile />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/visitors">
					<LayoutTwoColumn title="Visitors">
						<Visitors />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/visitors/:id">
					<LayoutTwoColumn title="Visitor profile" showBack={true}>
						<VisitorsProfile />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/requests-to-join">
					<LayoutTwoColumn title="Requests to join">
						<RequestsToJoin />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/requests-to-join/:id">
					<LayoutTwoColumn title="Profile">
						<RequestsToJoinDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/invite-to-join">
					<LayoutTwoColumn showLogo={true}>
						<InviteToJoin />
					</LayoutTwoColumn>
				</Route>

				<Route path="/join-organisation">
					<LayoutOneColumn>
						<AddCertification />
					</LayoutOneColumn>
				</Route>

				<Route path="/add-certification">
					<LayoutOneColumn>
						<AddCertification />
					</LayoutOneColumn>
				</Route>

				{/* RESOURCE SECTION */}
				<Route exact path="/child-safety-standards">
					<LayoutTwoColumn title="Child Safe Standards">
						<ChildSafeStandards />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/child-safety-standards/:id">
					<LayoutTwoColumn showBack={true}>
						<ChildSafeStandardsDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/policies">
					<LayoutTwoColumn title="Policies">
						<Policies />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/policies/user-view">
					<LayoutTwoColumn title="Policies">
						<Policies />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/policies/admin-view">
					<LayoutTwoColumn title="Policies">
						<Policies />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/policy-submit/:id">
					<LayoutTwoColumn title="Update policy" showBack={true}>
						<PolicySubmit />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/policy-templates">
					<LayoutTwoColumn title="Policy templates" showBack={true}>
						<PolicyTemplates />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/policy-submit">
					<LayoutTwoColumn title="Upload new policy" showBack={true}>
						<PolicySubmit />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/training">
					<LayoutTwoColumn title="Training">
						<Training />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/training/user-view">
					<LayoutTwoColumn title="Training">
						<Training />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/training/admin-view">
					<LayoutTwoColumn title="Training">
						<Training />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/training/:id">
					<LayoutTwoColumn showBack={true}>
						<TrainingDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/training-submit">
					<LayoutTwoColumn showBack={true} title="Add New Training Module">
						<TrainingSubmit />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/training-submit/:id">
					<LayoutTwoColumn showBack={true} title="Add New Training Module">
						<TrainingSubmit />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/safety-checklists">
					<LayoutTwoColumn title="Safety Checklists">
						<SafetyChecklists />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/safety-checklists/user-view">
					<LayoutTwoColumn title="Safety Checklists">
						<SafetyChecklists />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/safety-checklists/admin-view">
					<LayoutTwoColumn title="Safety Checklists">
						<SafetyChecklists />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/safety-checklists/:id">
					<LayoutTwoColumn showBack={true}>
						<SafetyChecklistsDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/new-checklist">
					<LayoutTwoColumn title="Create a new checklist" showBack={true}>
						<SafetyChecklistsNewChecklist />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/create-checklist">
					<LayoutTwoColumn>
						<SafetyChecklistsSubmit />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/create-checklist/:id">
					<LayoutTwoColumn>
						<SafetyChecklistsSubmit />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/preview-checklist/:id">
					<LayoutTwoColumn showBack={true}>
						<SafetyChecklistsTemplatesPreview />
					</LayoutTwoColumn>
				</Route>

				{/* ISSUES SECTION */}
				<Route exact path="/feedback">
					<LayoutTwoColumn title="Feedback">
						<Feedback />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/feedback/user-view">
					<LayoutTwoColumn title="Feedback">
						<Feedback />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/feedback/admin-view">
					<LayoutTwoColumn title="Feedback">
						<Feedback />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/feedback/:id">
					<LayoutTwoColumn showBack={true}>
						<FeedbackDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/feedback-submit">
					<LayoutTwoColumn>
						<FeedbackSubmit />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/incident-reports">
					<LayoutTwoColumn title="Incident reports">
						<IncidentReports />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/incident-reports/user-view">
					<LayoutTwoColumn title="Incident reports">
						<IncidentReports />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/incident-reports/admin-view">
					<LayoutTwoColumn title="Incident reports">
						<IncidentReports />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/incident-reports-archive/:id">
					<LayoutTwoColumn>
						<IncidentReportsDetailArchive />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/incident-reports/:id">
					<LayoutTwoColumn>
						<IncidentReportsDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/incident-submit-archive">
					<LayoutTwoColumn>
						<IncidentSubmitArchive />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/incident-submit-archive/:id">
					<LayoutTwoColumn>
						<IncidentSubmitArchive />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/incident-submit">
					<LayoutTwoColumn>
						<IncidentSubmit />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/incident-submit/:id">
					<LayoutTwoColumn>
						<IncidentSubmit />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/complaints">
					<LayoutTwoColumn title="Complaints">
						<Complaints />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/complaints/user-view">
					<LayoutTwoColumn title="Complaints">
						<Complaints />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/complaints/admin-view">
					<LayoutTwoColumn title="Complaints">
						<Complaints />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/complaints/:id">
					<LayoutTwoColumn>
						<ComplaintsDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/complaints-submit">
					<LayoutTwoColumn>
						<ComplaintsSubmit />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/complaints-advice-templates">
					<LayoutTwoColumn title="Complaints handling advice" showBack={true}>
						<ComplaintsAdviceTemplates />
					</LayoutTwoColumn>
				</Route>

				{/* AUDIT */}
				<Route exact path="/audit">
					<LayoutTwoColumn showLogo={true}>
						<Audit />
					</LayoutTwoColumn>
				</Route>

				{/* SETTINGS */}
				<Route exact path="/settings">
					<LayoutTwoColumn showLogo={true}>
						<Settings />
					</LayoutTwoColumn>
				</Route>

				{/* HELP DESK */}
				<Route exact path="/help-desk">
					<LayoutTwoColumn title="Help Desk">
						<HelpDesk />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/help-desk/:id">
					<LayoutTwoColumn showBack={true}>
						<HelpDeskDetail />
					</LayoutTwoColumn>
				</Route>

				{/* COMPANIES */}
				<Route exact path="/companies">
					<LayoutTwoColumn title="Companies">
						<Companies />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/companies/:id">
					<LayoutTwoColumn showBack={true}>
						<CompaniesDetail />
					</LayoutTwoColumn>
				</Route>

				{/* CONTENT */}
				<Route exact path="/content-training-module">
					<LayoutTwoColumn title="Training modules">
						<ContentTrainingModule />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-training-module-edit">
					<LayoutTwoColumn title="Update Training Module" showBack={true}>
						<ContentTrainingModuleDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-training-module-edit/:id">
					<LayoutTwoColumn title="Update Training Module" showBack={true}>
						<ContentTrainingModuleDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-policy-templates">
					<LayoutTwoColumn title="Policy Templates">
						<ContentPolicyTemplates />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-policy-templates-edit">
					<LayoutTwoColumn title="New Policy Template" showBack={true}>
						<ContentPolicyTemplatesDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-policy-templates-edit/:id">
					<LayoutTwoColumn title="Update Policy Template" showBack={true}>
						<ContentPolicyTemplatesDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-checklist-templates">
					<LayoutTwoColumn title="Checklist Templates">
						<ContentChecklistTemplates />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-checklist-templates-edit">
					<LayoutTwoColumn title="Create Checklist Template" showBack={true}>
						<ContentChecklistTemplatesDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-checklist-templates-edit/:id">
					<LayoutTwoColumn title="Edit Checklist Template" showBack={true}>
						<ContentChecklistTemplatesDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-complaints-advice">
					<LayoutTwoColumn title="Complaints Advice">
						<ContentComplaintsAdvice />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-complaints-advice-edit">
					<LayoutTwoColumn title="New Complaints Advice Documents" showBack={true}>
						<ContentComplaintsAdviceDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-complaints-advice-edit/:id">
					<LayoutTwoColumn title="Complaints Advice Documents" showBack={true}>
						<ContentComplaintsAdviceDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-child-safe-standards">
					<LayoutTwoColumn title="Child Safe Standards">
						<ContentChildSafeStandards />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-child-safe-edit">
					<LayoutTwoColumn title="Edit Child Safe Standards" showBack={true}>
						<ContentChildSafeStandardsDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-child-safe-edit/:id">
					<LayoutTwoColumn title="Edit Child Safe Standards" showBack={true}>
						<ContentChildSafeStandardsDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-help-desk">
					<LayoutTwoColumn title="Help Desk">
						<ContentHelpDesk />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-help-desk-edit">
					<LayoutTwoColumn title="Edit Help Desk Content" showBack={true}>
						<ContentHelpDeskDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/content-help-desk-edit/:id">
					<LayoutTwoColumn title="Edit Help Desk Content" showBack={true}>
						<ContentHelpDeskDetail />
					</LayoutTwoColumn>
				</Route>

				<Route exact path="/my-account">
					<LayoutTwoColumn title="My account">
						{/* <StaffProfile /> */}
						<MyAccount />
					</LayoutTwoColumn>
				</Route>

				{/* --- if none of the above routes are satisfied then redirect --- */}
				{user.loggedIn && (
					<Route path="/">
						<Redirect to="/dashboard" />
					</Route>
				)}
			</Switch>
		);
	}
};

export default App;
